/* eslint-disable @typescript-eslint/no-explicit-any */
import { useForm } from "react-hook-form"
import { z } from "zod"
import { zodResolver } from "@hookform/resolvers/zod"
import { useDispatch, useSelector } from "react-redux"
import { AppDispatch, RootState } from "@/redux"
import { useEffect, useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import { createCredentials, updateCredentials } from "@/services/credentialsService"
import { Form, FormLabel, FormMessage } from "@/components/ui/form"
import { Button } from "@/components/ui/button"
import { CredentialMethod } from "@/constants/credentials/credential"
import { CredentialDetailsResponseData, LoginMethod } from "@/types/credentials"
import { DynamicFieldRenderer } from "./components/DynamicFieldRenderer"
import { RadioInputField } from "@/components/custom/InputField/RadioInput"
import { UserRole } from "@/constants/users/userRole"

export const DynamicCredentialForm = ({
  loginMethods,
  method,
  appId,
  isFormModal = false,
  credentialDetails
}: {
  loginMethods: LoginMethod[]
  method: CredentialMethod
  appId: string
  isFormModal?: boolean
  credentialDetails?: CredentialDetailsResponseData
}) => {
  const dispatch: AppDispatch = useDispatch()
  const navigate = useNavigate()
  const { loading } = useSelector((state: RootState) => state.app)
  const { token, userData } = useSelector((state: RootState) => state.user)
  const isReadOnly = userData?.role === UserRole.USER
  const [selectedMethod, setSelectedMethod] = useState<LoginMethod | undefined>(loginMethods[0])

  const formSchema = useMemo(() => {
    if (!selectedMethod) return z.object({})
    const shape: Record<string, z.ZodTypeAny> = {
      nickname: z.string().min(1, "Please give this credential a nickname"),
    }
    selectedMethod.credentialAttributes.forEach(attr => {
      if (attr.attributeName.toLowerCase().includes("date")) {
        shape[attr.attributeName] = z.date({ required_error: `${attr.displayName} is required` })
      } else if (attr.isValueRedacted && method === CredentialMethod.EDIT) {
        // Write-only fields in edit mode are optional: blank = keep existing value
        shape[attr.attributeName] = z.string().optional().or(z.literal(''))
      } else {
        shape[attr.attributeName] = z.string().min(1, `${attr.displayName} is required`)
      }
    })
    return z.object(shape)
  }, [selectedMethod, method])

  const defaultValues = useMemo(() => {
    if (!selectedMethod) return {}
    return {
      nickname: "",
      ...Object.fromEntries(
        selectedMethod.credentialAttributes.map(attr => [
          attr.attributeName,
          attr.attributeName.toLowerCase().includes("date") ? new Date() : ""
        ])
      )
    }
  }, [selectedMethod])

  const form = useForm<{ nickname: string;[key: string]: any }>({
    resolver: zodResolver(formSchema),
    defaultValues
  })

  useEffect(() => {
    if (method === CredentialMethod.EDIT && credentialDetails) {
      const match = loginMethods.find(
        lm => lm.methodName === credentialDetails.loginMethodCode
      )
      if (match) {
        setSelectedMethod(match)
      }
    } else {
      setSelectedMethod(loginMethods[0])
    }
  }, [method, credentialDetails, loginMethods])

  // Set updated value to fields
  useEffect(() => {
    if (method === CredentialMethod.EDIT && credentialDetails) {
      const values: any = {
        nickname: credentialDetails.nickname
      }
      credentialDetails.credentialValues.forEach(cv => {
        // Find the corresponding field definition to check isValueRedacted
        const fieldDef = selectedMethod?.credentialAttributes.find(
          attr => attr.attributeName === cv.credentialAttributeCode
        )
        if (fieldDef?.isValueRedacted) {
          // Write-only field: do not pre-populate. Leave the form field empty.
          // The field already defaults to "" from defaultValues.
          return
        }
        // TODO Update this condition once we get type: Date for attributeField
        values[cv.credentialAttributeCode] = cv.credentialAttributeCode.toLowerCase().includes("date")
          ? new Date(cv.attributeValue)
          : cv.attributeValue
      })
      form.reset(values)
    } else {
      // Preserve existing nickname if user already typed it in
      const currentValues = form.getValues()
      form.reset({
        ...defaultValues,
        nickname: currentValues.nickname || ""
      })
    }
  }, [method, credentialDetails, defaultValues, form, selectedMethod])

  const onSubmit = async (values: any) => {
    if (!token || !selectedMethod) return

    const credentialValues = selectedMethod.credentialAttributes
      .filter(attr => {
        // Omit write-only fields that the user left blank (they did not intend to update them)
        if (attr.isValueRedacted && method === CredentialMethod.EDIT) {
          const val = values[attr.attributeName]
          return val !== undefined && val !== ''
        }
        return true
      })
      .map(attr => ({
        credentialAttributeCode: attr.attributeName,
        // TODO Update this condition once we get type: Date for attributeField
        attributeValue: attr.attributeName.toLowerCase().includes("date")
          ? new Date(values[attr.attributeName]).toISOString()
          : values[attr.attributeName],
      }))

    const body = {
      appId,
      loginMethodCode: selectedMethod.methodName,
      nickname: values.nickname,
      credentialValues,
    }

    const resp = method === CredentialMethod.CREATE
      ? await dispatch(createCredentials(token, body))
      : credentialDetails && await dispatch(updateCredentials(token, credentialDetails.id, body))

    if (resp?.data && !isFormModal) navigate("/credentials")
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-6 w-full">
        <DynamicFieldRenderer
          form={form}
          attributeName="nickname"
          displayName="Nickname"
          schema={formSchema}
          isDisabled={isReadOnly || loginMethods.length === 0}
          description="Give a unique name to identify this credential"
          placeholder="Cred****456"
        />

        {loginMethods.length > 1 && (
          <div className="flex flex-col items-start">
            <FormLabel className='flex justify-start font-semibold text-primary !text-sm' >
              Connection Type
            </FormLabel>
            <div className="flex flex-row items-center">
              {loginMethods.map(method => (
                <RadioInputField
                  key={method.methodName}
                  label={method.displayName}
                  value={method.methodName}
                  isChecked={selectedMethod?.methodName === method.methodName}
                  onChange={() => setSelectedMethod(method)}
                />
              ))}
            </div>
          </div>
        )}

        {selectedMethod?.credentialAttributes.map(attr => (
          <DynamicFieldRenderer
            key={attr.attributeName}
            form={form}
            attributeName={attr.attributeName}
            displayName={attr.displayName}
            description={attr.description}
            placeholder={attr.isValueRedacted ? "Enter new value to update" : attr.placeholder}
            schema={formSchema}
            isDisabled={isReadOnly}
          />
        ))}

        {loginMethods?.length === 0 && !loading ? (
          <FormMessage
            showIcon={true}
            className="flex flex-row justify-start"
          >
            We're sorry, but this application doesn't support credential management right now.
          </FormMessage>
        ) : null}

        <Button
          type="submit"
          className="w-fit font-semibold"
          disabled={isReadOnly || loginMethods.length === 0}
        >
          {method === CredentialMethod.CREATE ? "Create Credential" : "Update Credential"}
        </Button>
      </form>
    </Form>
  )
}
